import { useMemo, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { ArrowLeft, ShoppingCart, Check, Plus, FileText, Package } from 'lucide-react';
import { useTheme } from '@evi/skins';
import { fetchStoreCatalog, fetchStoreProduct, formatCents } from '@/lib/api';
import { useCartStore, selectItemCount } from '@/store/cart';
import { toast } from '@/store/toast';
import {
  familyForProduct, FAMILY_MANUALS, shippingLineFor, installmentLineFor,
} from './storeCatalog';
import { ManualCaptureSheet } from './ManualCaptureSheet';
import { EvieOrb } from '@/components/primitives';
import { useEvieChatStore } from '@/store/evieChat';
import styles from './Store.module.css';

interface Props { param: string; onNavigate: (path: string) => void }

/**
 * Deep-link product page (/store/product/:param, a slug or an id). The catalog page opens the
 * options sheet inline, so this route is the standalone fallback: a full page
 * that resolves the product's family (media + sibling variants) from the
 * catalog and mirrors the sheet's select-and-add flow.
 */
export function ProductDetailPage({ param, onNavigate }: Props) {
  const { t } = useTranslation('store');
  const { mode } = useTheme();
  const catalogQ = useQuery({ queryKey: ['store', 'catalog'], queryFn: fetchStoreCatalog, staleTime: 30_000 });
  const productQ = useQuery({ queryKey: ['store', 'product', param], queryFn: () => fetchStoreProduct(param), staleTime: 30_000 });
  const add = useCartStore((s) => s.add);
  const itemCount = useCartStore(selectItemCount);

  const product = productQ.data;
  const family = useMemo(
    () => (product ? familyForProduct(product, catalogQ.data ?? [product]) : null),
    [product, catalogQ.data],
  );

  const [selectedId, setSelectedId] = useState<string | null>(null);
  useEffect(() => { if (product) setSelectedId(product.id); }, [product]);
  const selected = family?.variants.find((v) => v.product.id === selectedId)
    ?? family?.variants.find((v) => v.product.id === product?.id)
    ?? family?.variants[0]
    ?? null;
  const multi = (family?.variants.length ?? 0) > 1;
  // Manual row keys on the FAMILY_MANUALS map — no entry, no row.
  const manual = family ? FAMILY_MANUALS[family.key] : undefined;
  const [manualOpen, setManualOpen] = useState(false);

  const isLoading = productQ.isLoading;
  const error = productQ.error;

  const handleAdd = (): void => {
    if (!selected) return;
    add(selected.product);
    toast(t('toast.added', { name: family?.title ?? selected.product.name }), { tone: 'accent' });
  };

  return (
    <div className={styles.store} data-store-theme={mode}>
      <header className={`${styles.head} ${styles.headSolid}`}>
        <button type="button" className={styles.headBtn} aria-label={t('header.back')} onClick={() => onNavigate('/store')}>
          <ArrowLeft size={18} />
        </button>
        <span className={`${styles.headTitle} ${styles.headTitleShown}`}>{t('header.title')}</span>
        <span className={styles.headActions}>
          <button
            type="button"
            className={styles.headBtn}
            aria-label={t('header.askEvie')}
            onClick={() => {
              useEvieChatStore.getState().setContext({ surface: 'store' });
              useEvieChatStore.getState().openSheet();
            }}
          >
            <EvieOrb size={18} />
          </button>
          <button type="button" className={styles.headBtn} aria-label={t('header.orders')} onClick={() => onNavigate('/store/orders')}>
            <Package size={18} />
          </button>
          <button type="button" className={styles.headBtn} aria-label={t('header.cart')} onClick={() => onNavigate('/store/cart')}>
            <ShoppingCart size={18} />
            {itemCount > 0 && <span className={styles.headCartCount}>{itemCount}</span>}
          </button>
        </span>
      </header>

      <div className={styles.body}>
        {isLoading && <div className={styles.grid}><div className={styles.skeleton} /></div>}

        {!isLoading && error && (
          <div className={styles.errorBox}>
            {error instanceof Error ? error.message : t('error.product')}
          </div>
        )}

        {!isLoading && !error && (!family || !selected) && (
          <div className={styles.emptyBox}>
            <p>{t('empty.product')}</p>
            <button type="button" className={styles.ghostBtn} onClick={() => onNavigate('/store')}>{t('backToStore')}</button>
          </div>
        )}

        {!isLoading && !error && family && selected && (
          <div className={styles.detail}>
            <div className={styles.sheetMedia}>
              {family.media?.film ? (
                <video className={styles.sheetVideo} src={family.media.film} poster={family.media.poster ?? family.media.hero} autoPlay muted loop playsInline />
              ) : family.media ? (
                <img className={styles.sheetImg} src={family.media.hero} alt={family.title} />
              ) : (
                <div className={styles.sheetImgFallback} aria-hidden />
              )}
            </div>

            <h1 className={styles.detailTitle}>{family.title}</h1>
            {(selected.product.description ?? family.blurb) && (
              <p className={styles.detailBlurb}>{selected.product.description ?? family.blurb}</p>
            )}

            {multi && (
              <div className={styles.optionList} role="radiogroup" aria-label={t('options.label')}>
                {family.variants.map((v) => {
                  const isSel = v.product.id === selected.product.id;
                  return (
                    <button
                      key={v.product.id}
                      type="button"
                      role="radio"
                      aria-checked={isSel}
                      className={`${styles.optionRow} ${isSel ? styles.optionRowSel : ''}`}
                      onClick={() => setSelectedId(v.product.id)}
                    >
                      <span className={styles.optionCheck} aria-hidden>{isSel && <Check size={13} />}</span>
                      <span className={styles.optionLabel}>{v.label}</span>
                      <span className={styles.optionPrice}>{formatCents(v.product.price_cents)}</span>
                    </button>
                  );
                })}
              </div>
            )}

            <div className={styles.sheetPriceRow}>
              <span className={styles.sheetPriceLabel}>{t('price')}</span>
              <span className={styles.sheetPrice}>
                {formatCents(selected.product.price_cents)} <span className={styles.usd}>USD</span>
              </span>
            </div>

            {/* Shipping class + installments — keyed on the SELECTED variant
                so each SKU shows its own per-unit rate (AC Ultra wall $149 or
                $199, its pedestal full set $299 freight per unit). The rule is
                the shared @evi/shared policy the server charges from. */}
            {selected.product.lead_time_label && (
              <p className={styles.installNote}>{selected.product.lead_time_label}</p>
            )}
            <p className={styles.shipNote}>{shippingLineFor(selected.product)}</p>
            <p className={styles.installNote}>{installmentLineFor(selected.product.price_cents)}</p>

            <button type="button" className={styles.sheetAdd} onClick={handleAdd}>
              <Plus size={16} /> {t('add')}
            </button>
            <button type="button" className={styles.sheetViewCart} onClick={() => onNavigate('/store/cart')}>
              <ShoppingCart size={15} /> {t('viewCart')}
            </button>

            {/* Download the manual — keyed on FAMILY_MANUALS, so a family
                without a PDF (AC Pro today) renders no row at all. The
                sheet captures an email lead first; the download itself is
                never held hostage to the lead write (Turnstile rejection
                excepted). */}
            {manual && (
              <button
                type="button"
                className={styles.sheetViewCart}
                onClick={() => setManualOpen(true)}
              >
                <FileText size={15} /> {t('downloadManual')}
              </button>
            )}
          </div>
        )}
      </div>

      {manual && (
        <ManualCaptureSheet
          isOpen={manualOpen}
          onClose={() => setManualOpen(false)}
          manual={manual}
        />
      )}
    </div>
  );
}
